import type { SourcingEventStatus } from "../../autogen/bff-api";
import { STATUS_BUCKET, statusesForBuckets } from "../layout/statusBuckets";
import type { ArgAdapter, ContextCheck, ToolContext, ToolResult } from "./tools";

const BODY_KEY = "createSourcingEventRequest";

const field = (args: Record<string, unknown>, name: string): unknown => {
  const direct = args[name];
  if (direct !== undefined) return direct;
  const nested = args[BODY_KEY];
  return nested && typeof nested === "object" ? (nested as Record<string, unknown>)[name] : undefined;
};

const normalize = (value: string) => value.trim().toLowerCase();

const matchDepartment = (ctx: ToolContext, name: unknown) =>
  typeof name === "string"
    ? ctx.auth.eventDepartments.find((d) => normalize(d.name) === normalize(name))
    : undefined;

const departmentNames = (ctx: ToolContext) => ctx.auth.eventDepartments.map((d) => d.name).join(", ");

export const sourcingContextChecks: Record<string, ContextCheck> = {
  createSourcingEvent: (args, ctx) => {
    const named = field(args, "homeDepartment");
    if (matchDepartment(ctx, named)) return null;
    if (ctx.auth.eventDepartments.length === 0) {
      return refuse("NO_DEPARTMENT", "Du kan ikke opprette anskaffelser i noen avdeling i denne organisasjonen.");
    }
    return refuse(
      "UNKNOWN_DEPARTMENT",
      typeof named === "string" && named.trim()
        ? `Fant ingen avdeling som heter «${named}» du kan opprette anskaffelser i. Velg blant: ${departmentNames(ctx)}.`
        : `Spør brukeren hvilken avdeling anskaffelsen skal ligge under. Velg blant: ${departmentNames(ctx)}.`,
    );
  },
};

export const sourcingArgAdapters: Record<string, ArgAdapter> = {
  listBuyerEvents: (args) => {
    const statuses = Array.isArray(args.statuses) ? args.statuses.filter((s): s is string => typeof s === "string") : [];
    if (statuses.length === 0) return args;
    const buckets = Array.from(new Set(statuses.map((s) => STATUS_BUCKET[s as SourcingEventStatus]).filter(Boolean)));
    return { ...args, statuses: statusesForBuckets(buckets) };
  },
  createSourcingEvent: (args, ctx) => {
    const projects = withNewProcurementProject(projectIds(field(args, "projects")), ctx);
    return {
      [BODY_KEY]: {
        title: typeof field(args, "title") === "string" ? field(args, "title") : "",
        homeDepartmentId: matchDepartment(ctx, field(args, "homeDepartment"))?.id,
        ...(Array.isArray(field(args, "fieldSources")) ? { fieldSources: field(args, "fieldSources") } : {}),
        ...(projects.length > 0 ? { projects } : {}),
        ...(ctx.createsProcurementWithAi ? { inInterview: true } : {}),
      },
    };
  },
};

const withNewProcurementProject = (projects: string[], ctx: ToolContext): string[] =>
  ctx.newProcurementProjectId && !projects.includes(ctx.newProcurementProjectId)
    ? [...projects, ctx.newProcurementProjectId]
    : projects;

const projectIds = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((id): id is string => typeof id === "string") : [];

const refuse = (deviationCode: string, message: string): ToolResult => ({ ok: false, deviationCode, message });
